import { genVr } from "../../core/utils";
import { BaseEntity, CacheBase, EntityIndex, ModelEntity, ParentEntity, PropagateEntity } from "../../core/baseEntity.model";
import { AuthService } from "../core-services/auth.service";
import { PaymentPropagateTrait } from "../payment/payment.entity";
import { UserCache } from "../account/account.entity";
import { FeedCache } from "../feed/feed.entity";

export type PaymentPlanType = "month" | "session";
export interface ClientTrait {
    id: string;
    name: string;
    email?: string;
    phone?: string;
    paymentPlan: PaymentPlanType;
    paymentDay: number;
    
    nextAppointmentHourNotification?: number;
    wsNextAppointmentHourNotification?: boolean;
    emailNextAppointmentHourNotification?: boolean;

    pendingPaymentNotification?: number;
    wsPendingPaymentNotification?: boolean;
    emailPendingPaymentNotification?: boolean;

    // mobile: string;
    address?: string;
    // city?: string;
    // state?: string;
    // country?: string;
    createdAt: number;
    price?: number;
    payments?: PaymentPropagateTrait[];

    uName: string;
}

function getDefaultEmail(this: ClientEntity) { return `${this.idxName?.replace(/ /g, '_')}@careadmin.co`; }

@ModelEntity("clients")
export class ClientEntity extends BaseEntity<ClientEntity> implements ClientTrait {
    @ParentEntity("users", () => {
        console.log("client user id", AuthService.instance?.currentUser?.uid)
        return AuthService.instance?.currentUser?.uid;
    })
    public userId: string;

    @EntityIndex({
        default: getDefaultEmail
    })
    public email: string;

    public readonly idxName: string;

    public readonly idxNameSearch: string[] = [];

    public payments: PaymentPropagateTrait[] = [];

    public paymentPlan: PaymentPlanType = 'session';
    public paymentDay: number;

    public nextAppointmentHourNotification?: number;
    public wsNextAppointmentHourNotification?: boolean;
    public emailNextAppointmentHourNotification?: boolean;

    public pendingPaymentNotification?: number;
    public wsPendingPaymentNotification?: boolean;
    public emailPendingPaymentNotification?: boolean;
    public id: string;
    public name: string;
    public phone?: string;
    public address?: string;
    public city?: string;
    public state?: string;
    public country?: string;
    public price: number = 0;
    public createdAt: number = Date.now();
    public version: string = genVr();

    public uName: string;
    public language: string = 'pt_BR';

    constructor(data?: {
        id: string;
        name: string;
        email?: string;
        phone?: string;
        address?: string;
        price?: number;
        paymentPlan: PaymentPlanType;
        paymentDay: number;
    
        nextAppointmentHourNotification?: number;
        wsNextAppointmentHourNotification?: boolean;
        emailNextAppointmentHourNotification?: boolean;
    
        pendingPaymentNotification?: number;
        wsPendingPaymentNotification?: boolean;
        emailPendingPaymentNotification?: boolean;
        
    }) {
        super();
        
        this.id = data?.id;
        this.name = data?.name;
        this.email = data?.email;
        this.phone = data?.phone;
        this.address = data?.address;
        this.price = data?.price || 0;
        this.paymentPlan = data?.paymentPlan || 'session';
        this.paymentDay = data?.paymentDay || 1;
        this.nextAppointmentHourNotification = data?.nextAppointmentHourNotification;
        this.wsNextAppointmentHourNotification = data?.wsNextAppointmentHourNotification;
        this.emailNextAppointmentHourNotification = data?.emailNextAppointmentHourNotification;
        this.pendingPaymentNotification = data?.pendingPaymentNotification;
        this.wsPendingPaymentNotification = data?.wsPendingPaymentNotification;
        this.emailPendingPaymentNotification = data?.emailPendingPaymentNotification;
        this.createdAt = Date.now();
        this.version = genVr();
        
        this.idxName = data?.name?.toLowerCase();
        if (this.idxName) {
            const splitted = this.idxName?.split(' ');
            this.idxNameSearch = splitted;
            let idxName = splitted[0];
            const tot = splitted.length;
            for (let i = 1;i<tot;i++) {
                idxName += ` ${splitted[i]}`;
                this.idxNameSearch.push(idxName);
            }
        }
        // this.idxNameSearch.push(this.idxName);
 
    }

    static async loadAll<C extends BaseEntity<C>>(
        id: string = AuthService.instance?.currentUser?.uid, 
        {parentName, offset, ignoreStorageDbData, orderBy}: {
            parentName?: string; 
            offset?: string; 
            ignoreStorageDbData?: boolean; 
            orderBy?: {
            column: keyof C;
            sort: 'asc' | 'desc'
        }} = {parentName: 'users'}): Promise<C[]> {

        super.setParent('users', id);
        return super.loadAll(id, {parentName: parentName || 'users', offset, orderBy, ignoreStorageDbData: ignoreStorageDbData || true});
    }

    static async persistMany<T = ClientEntity>(data: T[]): Promise<boolean> {
        const id = AuthService.instance?.currentUser?.uid;
        super.setParent('users', id);
        return super.persistMany<T>(data, id)
    }  
    public export<T = ClientTrait>(): T {
        return {
            id: this.id,
            version: this.version,
            name: this.name,
            email: this.email,
            phone: this.phone,
            address: this.address,
            createdAt: this.createdAt,
            idxName: this.idxName,
            idxNameSearch: this.idxNameSearch,
            pendingSync: this.pendingSync,
            price: this.price,
            payments: this.payments,
            paymentPlan: this.paymentPlan,
            paymentDay: this.paymentDay,
            nextAppointmentHourNotification: this.nextAppointmentHourNotification || 0,
            wsNextAppointmentHourNotification: !!this.wsNextAppointmentHourNotification,
            emailNextAppointmentHourNotification: !!this.emailNextAppointmentHourNotification,
            pendingPaymentNotification: this.pendingPaymentNotification || 0,
            wsPendingPaymentNotification: !!this.wsPendingPaymentNotification,
            emailPendingPaymentNotification: !!this.emailPendingPaymentNotification,
            
            uName: this.uName,
            language: this.language,
            
        } as any;
    }

    public async postLoad(): Promise<this> {
        const payments = {};
        if (this.payments?.length > 0) {
            for (const payment of this.payments) {
                payments[payment.id] = !payments[payment.id] || payments[payment.id].version < payment.version ? payment : payments[payment.id];
            }
            this.payments = Object.values(payments);
        }

        return this;
    }
}

export const ClientCache = class extends CacheBase {
    static get canonical() {
       return 'c';
    }

    static ref(clientId: string, id: string = AuthService.instance.currentUser?.uid) {
        return `${UserCache.ref(id)}/${this.canonical}/${clientId}`;
    }   

    static feedRef(clientId: string, id: string = AuthService.instance.currentUser?.uid) {
        return `${this.ref(clientId, id)}/${FeedCache.canonical}`;
    }  

    static agendaRef(clientId: string, path?: 'day' | 'month', id: string = AuthService.instance.currentUser?.uid) {
        const short = {
            day: 'd',
            month: 'm'
        };
        return `${this.ref(clientId, id)}/ag` + (path? `/${short[path]}` : '');
    }
    
}